import { Component, HostListener, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PdfViewerComponent } from 'ng2-pdf-viewer';

export interface DocPreviewData {
  url: string;            // signed/public URL of the source document
  name: string;          // display name, e.g. "Discharge_Summary_07.pdf"
  searchTerm?: string;   // initial term to find + highlight (e.g. an AI-extracted value)
  contextLabel?: string; // small subtitle, e.g. "Finding value for: SSN"
}

/**
 * Generic source-document preview dialog. Renders the file in a near-full-screen
 * overlay and — for PDFs — auto-searches for `searchTerm` and highlights every
 * occurrence so the user can verify AI-extracted data against the source.
 *
 * Mirrors the medication doc-preview dialog in the Core module, but lives in the
 * shared module so any shared component (e.g. the Waystar popup) can use it.
 *
 * Match navigation:
 *  - Enter         -> next match
 *  - Shift+Enter   -> previous match
 *  - Esc           -> close (handled by MatDialog)
 *  - prev/next buttons in the header do the same
 */
@Component({
  selector: 'app-doc-preview-dialog',
  templateUrl: './doc-preview-dialog.component.html',
  styleUrls: ['./doc-preview-dialog.component.scss']
})
export class DocPreviewDialogComponent {
  @ViewChild(PdfViewerComponent) pdfComponent: PdfViewerComponent;

  // ---- File metadata ----
  documentpath: string;
  documentName: string;
  contextLabel: string;
  documentType: string;

  // ---- Search / match state (PDFs only) ----
  searchText: string = '';
  matchCount: number = 0;
  currentMatchIndex: number = 0; // 1-based for display ("3 of 7"); 0 means no current match
  isSearching: boolean = false;  // true while PDF.js is scanning pages for matches
  pdfLoaded: boolean = false;
  pdfTotalPages: number = 0;
  currentPage: number = 1;
  /** True once a text layer has rendered at least once — find needs this. */
  private textLayerReady: boolean = false;
  /** Remembers the last query so we can detect "fresh find" vs "find next". */
  private lastFindQuery: string = '';
  /** True once we've attached our own listeners to the PDF.js event bus. */
  private eventBusWired: boolean = false;

  private static readonly KNOWN_EXTS = [
    'pdf',
    'doc', 'docx', 'txt', 'ppt', 'pptx', 'xls', 'xlsx', 'csv',
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'
  ];

  constructor(
    public dialogRef: MatDialogRef<DocPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocPreviewData
  ) {
    this.documentpath = data?.url || '';
    this.documentName = data?.name || 'Document';
    this.contextLabel = data?.contextLabel || '';
    this.documentType = this.deriveType(this.documentName, this.documentpath);

    // Seed the search box with the term to locate (the AI-extracted value),
    // verbatim — we want every reference of the full value, not just a word.
    const term = (data?.searchTerm || '').trim();
    if (this.isPdf() && term) {
      this.searchText = term;
      // Spinner shows from open through PDF load + text-layer render + scan,
      // flipping off only when the find controller emits its final state.
      this.isSearching = true;
    }
  }

  // =================== File type detection ===================

  private deriveType(name: string, url: string): string {
    const urlExt = this.extFromString(url);
    if (urlExt && DocPreviewDialogComponent.KNOWN_EXTS.includes(urlExt)) {
      return urlExt;
    }
    const nameExt = this.extFromString(name);
    if (nameExt && DocPreviewDialogComponent.KNOWN_EXTS.includes(nameExt)) {
      return nameExt;
    }
    if (url) return 'pdf'; // fallback: clinical source docs are almost always PDFs
    return '';
  }

  private extFromString(s: string): string {
    if (!s) return '';
    const cleaned = s.toLowerCase().split('?')[0].split('#')[0];
    const lastDot = cleaned.lastIndexOf('.');
    if (lastDot < 0) return '';
    const ext = cleaned.substring(lastDot + 1);
    if (ext.length === 0 || ext.length > 5 || ext.indexOf('/') >= 0) return '';
    return ext;
  }

  isOfficeDoc(): boolean {
    return ['doc', 'docx', 'txt', 'ppt', 'pptx', 'xls', 'xlsx', 'csv'].includes(this.documentType);
  }
  isPdf(): boolean { return this.documentType === 'pdf'; }
  isImage(): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(this.documentType);
  }

  // =================== PDF lifecycle ===================

  /** Called by ng2-pdf-viewer once the PDF document is parsed. */
  onPdfLoaded(pdf: any): void {
    this.pdfLoaded = true;
    this.pdfTotalPages = pdf?.numPages || 0;
  }

  /**
   * Fires once each page's text layer is painted. PDF.js's find controller can
   * only locate matches inside rendered text layers, so we hold off the initial
   * auto-search until at least the first page is ready.
   */
  onTextLayerRendered(_event: any): void {
    this.wireEventBusOnce();

    if (this.textLayerReady) return;
    this.textLayerReady = true;
    if (this.searchText) {
      // 50ms gives the find controller time to register the rendered text.
      setTimeout(() => this.executeFind(this.searchText, false, false), 50);
    }
  }

  /**
   * Attach our own listeners to PDF.js's event bus so we reliably get match
   * counts and progress states — ng2-pdf-viewer's Angular outputs for these
   * are flaky across versions.
   */
  private wireEventBusOnce(): void {
    if (this.eventBusWired) return;
    const pdfAny: any = this.pdfComponent as any;
    const eventBus = pdfAny.eventBus
      || pdfAny.pdfFindController?.eventBus
      || pdfAny.pdfViewer?.eventBus
      || pdfAny.pdfViewer?._eventBus;
    if (!eventBus || typeof eventBus.on !== 'function') return;

    eventBus.on('updatefindmatchescount', (evt: any) => {
      const counts = evt?.matchesCount || evt;
      if (counts && typeof counts.total === 'number') {
        this.matchCount = counts.total || 0;
        this.currentMatchIndex = counts.current || (this.matchCount > 0 ? 1 : 0);
      }
    });

    eventBus.on('updatefindcontrolstate', (evt: any) => {
      const state = evt?.state;
      this.isSearching = state === 3; // PENDING
      if (state === 1) { // NOT_FOUND
        this.matchCount = 0;
        this.currentMatchIndex = 0;
      }
      const counts = evt?.matchesCount;
      if (counts && typeof counts.total === 'number') {
        this.matchCount = counts.total || 0;
        this.currentMatchIndex = counts.current || (this.matchCount > 0 ? 1 : 0);
      }
    });

    this.eventBusWired = true;
  }

  /** Fires whenever ng2-pdf-viewer scrolls / changes the visible page. */
  onPdfPageChange(page: number): void {
    if (page > 0) this.currentPage = page;
  }

  /** PDF.js find controller emits this when match state changes. */
  onFindMatch(event: any): void {
    const counts = event?.matchesCount || event;
    if (counts && typeof counts.total === 'number') {
      this.matchCount = counts.total || 0;
      this.currentMatchIndex = counts.current || (this.matchCount > 0 ? 1 : 0);
    }
    if (typeof event?.state === 'number') {
      // FindState: FOUND=0, NOT_FOUND=1, WRAPPED=2, PENDING=3
      if (event.state === 1) {
        this.matchCount = 0;
        this.currentMatchIndex = 0;
      }
    }
  }

  /**
   * The single entry point that talks to PDF.js's find controller. Tries the
   * legacy `executeCommand` first (ng2-pdf-viewer 6.x normalizes it), then
   * falls back to the modern eventBus dispatch.
   */
  private executeFind(query: string, isAgain: boolean, findPrevious: boolean): void {
    if (!this.pdfComponent || !this.isPdf()) return;
    const term = (query || '').trim();
    if (!term) {
      this.matchCount = 0;
      this.currentMatchIndex = 0;
      this.lastFindQuery = '';
      this.isSearching = false;
      return;
    }

    if (!isAgain) {
      this.isSearching = true;
      this.matchCount = 0;
      this.currentMatchIndex = 0;
    }

    this.wireEventBusOnce();

    const pdfAny: any = this.pdfComponent as any;
    const findController = pdfAny.pdfFindController
      || pdfAny.pdfViewer?.findController
      || pdfAny.pdfViewer?._findController;
    const eventBus = pdfAny.eventBus
      || findController?.eventBus
      || pdfAny.pdfViewer?.eventBus;

    const params: any = {
      query: term,
      caseSensitive: false,
      entireWord: false,
      phraseSearch: true,
      highlightAll: true,
      findPrevious: findPrevious
    };

    if (findController && typeof findController.executeCommand === 'function') {
      findController.executeCommand(isAgain ? 'findagain' : 'find', params);
      this.lastFindQuery = term;
      return;
    }

    if (eventBus && typeof eventBus.dispatch === 'function') {
      eventBus.dispatch('find', {
        source: this.pdfComponent,
        type: isAgain ? 'again' : '',
        ...params
      });
      this.lastFindQuery = term;
      return;
    }
  }

  /** Called when the user edits the search box. Treats it as a fresh search. */
  onSearchTextChange(): void {
    this.executeFind(this.searchText, false, false);
  }

  /** Next match — "find again" forwards. */
  nextMatch(): void {
    const fresh = (this.searchText || '').trim() !== this.lastFindQuery;
    this.executeFind(this.searchText, !fresh, false);
  }

  /** Previous match — "find again" backwards. */
  prevMatch(): void {
    const fresh = (this.searchText || '').trim() !== this.lastFindQuery;
    this.executeFind(this.searchText, !fresh, true);
  }

  clearSearch(): void {
    this.searchText = '';
    this.executeFind('', false, false);
  }

  // =================== Keyboard shortcuts ===================

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(e: KeyboardEvent): void {
    if (!this.isPdf() || !this.searchText) return;
    e.preventDefault();
    this.nextMatch();
  }

  @HostListener('document:keydown.shift.enter', ['$event'])
  onShiftEnterKey(e: KeyboardEvent): void {
    if (!this.isPdf() || !this.searchText) return;
    e.preventDefault();
    this.prevMatch();
  }

  // (Esc is handled by MatDialog itself.)

  // =================== Buttons ===================

  download(): void {
    if (!this.documentpath) return;
    const a = document.createElement('a');
    a.href = this.documentpath;
    a.download = this.documentName || 'document';
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  openInNewTab(): void {
    if (this.documentpath) {
      window.open(this.documentpath, '_blank', 'noopener');
    }
  }

  close(): void { this.dialogRef.close(); }
}
